/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Drawer, Select, Modal, message, Tooltip, DatePicker } from 'antd';
import { ExportOutlined, RedoOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { FilterTitle } from 'components/common';
import FilterTag from 'components/common/FilterTag';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { getTableColumns, StatusOptions, MovementTypeOptions } from './RouteListPageV2.config';
import { getRouteListV2, updateRouteStatusV2, exportRouteCSVV2 } from 'store/routeSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { usePersistedState } from 'hooks';

const { Search } = Input;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const CreateButton = styled(FilterAtnButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	padding: 0 24px;
	min-width: 120px;
	height: 40px;
	// border-radius: 8px;
	border: none;
	font-weight: 500;
	letter-spacing: 0.5px;

	&:hover {
		background-color: #1a4a6e !important;
		color: #fff !important;
		border: none !important;
	}
`;

export default function RouteListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('routeList:search', '');
	const [searchTerm, setSearchTerm] = usePersistedState('routeList:searchTerm', '');
	const [loading, setLoading] = useState(false);
	const [routeData, setRouteData] = useState([]);
	const [pagination, setPagination] = usePersistedState('routeList:pagination', {
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [filters, setFilters] = usePersistedState('routeList:filters', {});
	const [sortBy, setSortBy] = usePersistedState('routeList:sortBy', null);
	const [statusModalVisible, setStatusModalVisible] = useState(false);
	const [selectedRoute, setSelectedRoute] = useState(null);
	const [newStatus, setNewStatus] = useState(null);
	const [updatingStatus, setUpdatingStatus] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [dateRange, setDateRange] = useState(null);
	const [projects, setProjects] = useState([]);
	const [selectedProject, setSelectedProject] = usePersistedState('routeList:project', undefined);
	const [loadingProjects, setLoadingProjects] = useState(false);

	// Fetch projects for filter dropdown
	const fetchProjects = async () => {
		setLoadingProjects(true);
		try {
			const result = await dispatch(
				getProjectListV2({
					filter_by: {},
					pagination: { page: 1, per_page: 100 },
				})
			);
			if (result?.rows) {
				setProjects(
					result.rows.map((p) => ({
						value: p.id,
						label: p.project_name,
					}))
				);
			}
		} catch (error) {
			console.error('Error fetching projects:', error);
		} finally {
			setLoadingProjects(false);
		}
	};

	// Fetch projects when filter drawer opens
	useEffect(() => {
		if (filterDrawerOpen && projects.length === 0) {
			fetchProjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen]);

	const buildFilterBy = () => ({
		...(searchTerm && { route_name: searchTerm }),
		...(filters?.movement_type && { movement_type: filters.movement_type }),
		...(filters?.is_active !== undefined && { is_active: filters.is_active }),
		...(filters?.created_from && { created_from: filters.created_from }),
		...(filters?.created_to && { created_to: filters.created_to }),
		...(selectedProject && { project_id: selectedProject }),
	});

	const fetchRoutes = async () => {
		setLoading(true);
		try {
			const result = await dispatch(
				getRouteListV2({
					filter_by: buildFilterBy(),
					pagination: {
						page: pagination?.current || 1,
						per_page: pagination?.pageSize || 10,
					},
					sort_by: sortBy || null,
				})
			);

			const rows = result?.rows || [];

			// Use project data from the route response
			const enrichedRows = rows.map((row) => ({
				...row,
				project_name:
					row.project_name ??
					row.project?.project_name ??
					row.project_code ??
					row.project?.project_code ??
					'-',
			}));

			setRouteData(enrichedRows);
			setPagination((prev) => ({
				...prev,
				total: result?.count || 0,
			}));
		} catch (error) {
			console.error('Error fetching routes:', error);
			message.error('Failed to fetch routes. Please try again.');
			setRouteData([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchRoutes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current, pagination.pageSize, filters, sortBy, searchTerm, selectedProject]);

	const handleSearch = (value) => {
		const trimmedValue = value ? value.trim() : '';
		setSearchValue(trimmedValue);
		setSearchTerm(trimmedValue);
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleTableChange = (paginationConfig, tableFilters, sorter) => {
		setPagination({
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
			total: paginationConfig.total,
		});

		if (sorter && sorter.field) {
			const sortableFields = ['route_name', 'created_at', 'movement_type'];
			if (sortableFields.includes(sorter.field)) {
				setSortBy({
					field: sorter.field,
					order: sorter.order === 'ascend' ? 'asc' : 'desc',
				});
			} else {
				setSortBy(null);
			}
		} else {
			setSortBy(null);
		}
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			await dispatch(
				exportRouteCSVV2({
					filter_by: buildFilterBy(),
				})
			);
		} catch (error) {
			message.error('Failed to export routes. Please try again.');
		} finally {
			setExporting(false);
		}
	};

	const handleSync = () => {
		fetchRoutes();
	};

	const handleCreate = () => {
		navigate('/routes/create');
	};

	const handleFilterChange = (filterKey, value) => {
		setFilters((prev) => ({ ...prev, [filterKey]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleResetFilters = () => {
		setFilters({});
		setSearchValue('');
		setSearchTerm('');
		setSortBy(null);
		setDateRange(null);
		setSelectedProject(undefined);
		setPagination((prev) => ({ ...prev, current: 1 }));
		setFilterDrawerOpen(false);
	};

	const hasMovementTypeFilter = () =>
		filters.movement_type !== undefined && filters.movement_type !== '';
	const hasStatusFilter = () => filters.is_active !== undefined && filters.is_active !== '';
	const hasProjectFilter = () =>
		selectedProject !== undefined && selectedProject !== null && selectedProject !== '';
	const hasDateRangeFilter = () => filters.created_from && filters.created_to;

	const activeFiltersCount = [
		hasMovementTypeFilter(),
		hasStatusFilter(),
		hasProjectFilter(),
		hasDateRangeFilter(),
	].filter(Boolean).length;

	const handleRemoveMovementTypeFilter = () => handleFilterChange('movement_type', undefined);
	const handleRemoveStatusFilter = () => handleFilterChange('is_active', undefined);
	const handleRemoveProjectFilter = () => {
		setSelectedProject(undefined);
		setPagination((prev) => ({ ...prev, current: 1 }));
	};
	const handleRemoveDateRangeFilter = () => {
		setDateRange(null);
		setFilters((prev) => {
			const newFilters = { ...prev };
			delete newFilters.created_from;
			delete newFilters.created_to;
			return newFilters;
		});
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleStatusToggle = (route, newStatusValue) => {
		if (!route?.id) {
			message.error('Invalid route data');
			return;
		}
		setSelectedRoute(route);
		setNewStatus(newStatusValue);
		setStatusModalVisible(true);
	};

	const handleStatusUpdateConfirm = async () => {
		if (!selectedRoute?.id) return;

		setUpdatingStatus(true);
		try {
			await dispatch(
				updateRouteStatusV2({
					id: selectedRoute.id,
					is_active: newStatus ?? false,
				})
			);
			setStatusModalVisible(false);
			setSelectedRoute(null);
			setNewStatus(null);
			fetchRoutes();
		} catch (error) {
			if (!error?.response) {
				message.error('Failed to update route status.');
			}
		} finally {
			setUpdatingStatus(false);
		}
	};

	const handleStatusUpdateCancel = () => {
		setStatusModalVisible(false);
		setSelectedRoute(null);
		setNewStatus(null);
	};

	return (
		<Box>
			<Flex justifyContent="space-between" mt={3}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search by Route Name"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<FilterAtnButton onClick={() => setFilterDrawerOpen(true)}>
						FILTERS
					</FilterAtnButton>
					<FilterDisplayBox>
						{hasMovementTypeFilter() && (
							<FilterTag onClose={handleRemoveMovementTypeFilter}>
								Movement Type
							</FilterTag>
						)}
						{hasStatusFilter() && (
							<FilterTag onClose={handleRemoveStatusFilter}>Status</FilterTag>
						)}
						{hasProjectFilter() && (
							<FilterTag onClose={handleRemoveProjectFilter}>Project</FilterTag>
						)}
						{hasDateRangeFilter() && (
							<FilterTag onClose={handleRemoveDateRangeFilter}>Date Range</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleResetFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="15px">
					<ButtonWrapper
						onClick={handleExport}
						icons={<ExportOutlined />}
						loading={exporting}
						disabled={exporting}
					>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<CreateButton onClick={handleCreate}>
						<PlusOutlined mr="5px" />
						<span> CREATE</span>
					</CreateButton>
				</Flex>
			</Flex>

			<Box mt={3}>
				<AntdTable
					rowKey="id"
					columns={getTableColumns(navigate, handleStatusToggle)}
					dataSource={routeData}
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						pageSizeOptions: ['10', '20', '50', '100'],
						showTotal: (total) =>
							`1-${Math.min(pagination.pageSize, total)} of ${total}`,
					}}
					onChange={handleTableChange}
				/>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle title="Filters" onClose={() => setFilterDrawerOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px" display="block">
							Status
						</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							value={filters.is_active}
							onChange={(val) => handleFilterChange('is_active', val)}
							allowClear
							placeholder="Select"
							options={StatusOptions}
						/>
					</Box>

					<Box mb="2rem">
						<Text mb="5px" display="block">
							Movement Type
						</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							value={filters.movement_type}
							onChange={(val) => handleFilterChange('movement_type', val)}
							allowClear
							placeholder="Select"
							options={MovementTypeOptions}
						/>
					</Box>

					{/* Project Filter */}
					<Box mb="2rem">
						<Text mb="5px" display="block">
							Project
						</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							value={selectedProject}
							onChange={(val) => {
								setSelectedProject(val);
								setPagination((prev) => ({ ...prev, current: 1 }));
							}}
							allowClear
							loading={loadingProjects}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
							placeholder="Select Project"
							options={projects}
						/>
					</Box>

					{/* Date Range Filter */}
					<Box mb="2rem">
						<Text mb="5px" display="block">
							Created At
						</Text>
						<RangePicker
							style={{ width: '100%' }}
							placeholder={['Start Date', 'End Date']}
							value={dateRange}
							format="DD/MM/YYYY"
							onChange={(dates) => {
								setDateRange(dates);
								setPagination((prev) => ({ ...prev, current: 1 }));
								if (dates && dates[0] && dates[1]) {
									setFilters((prev) => ({
										...prev,
										created_from: moment(dates[0]).startOf('day').toISOString(),
										created_to: moment(dates[1]).endOf('day').toISOString(),
									}));
								} else {
									setFilters((prev) => {
										const newFilters = { ...prev };
										delete newFilters.created_from;
										delete newFilters.created_to;
										return newFilters;
									});
								}
							}}
						/>
					</Box>

					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={handleResetFilters}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>

			<Modal
				title="Confirm Status Change"
				open={statusModalVisible}
				onOk={handleStatusUpdateConfirm}
				onCancel={handleStatusUpdateCancel}
				confirmLoading={updatingStatus}
				okText="Confirm"
				cancelText="Cancel"
			>
				<Text>
					Are you sure you want to {newStatus ? 'activate' : 'deactivate'} the route{' '}
					<strong>{selectedRoute?.route_name}</strong>?
				</Text>
			</Modal>
		</Box>
	);
}
